<script>
  import { Loading } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { getApplicableFilters } from "../../../../../lib/util/getDamApplicableFilters";
  import { pmfilters } from "lib/stores.js";

  export let partitionlabel;
  export let analysisid;
  export let unifiedlabel;
  export let scale = 1;
  export let drawUIElements = true;
  export let drawClicks = true;
  export let drawScreenshot = true;
  export let triggerUpdate = false;

  export function downloadSVG() {
    if (!windowdata || !canvas) return;

    const width = windowdata.windowwidth;
    const height = windowdata.windowheight;
    const scaleFactor = scale / 50;

    const svgWidth =
      screenshot && screenshot.complete && drawScreenshot
        ? screenshot.width
        : canvas.width;
    const svgHeight =
      screenshot && screenshot.complete && drawScreenshot
        ? screenshot.height
        : canvas.height;

    let svg = `<svg width="${svgWidth}" height="${svgHeight}" xmlns="http://www.w3.org/2000/svg">`;

    // background image
    if (screenshot && screenshot.complete && drawScreenshot) {
      const canvas2d = document.createElement("canvas");
      canvas2d.width = screenshot.width;
      canvas2d.height = screenshot.height;
      const ctx2d = canvas2d.getContext("2d");
      ctx2d.drawImage(screenshot, 0, 0);
      const base64 = canvas2d.toDataURL("image/png");

      svg += `<image href="${base64}" width="${svgWidth}" height="${svgHeight}"/>`;
    }

    // Window edge
    let fill =
      screenshot && screenshot.complete && drawScreenshot ? "none" : "white";
    svg += `<rect x="0" y="0" width="${width * scaleFactor}" height="${height * scaleFactor}" 
          fill="${fill}" stroke="black" stroke-width="1" />`;

    // UI elements
    if (drawUIElements) {
      allrects.forEach((rect) => {
        svg += `<rect x="${rect.positionx * scaleFactor}" y="${rect.positiony * scaleFactor}" 
              width="${rect.width * scaleFactor}" height="${rect.height * scaleFactor}" 
              fill="none" stroke="blue" stroke-width="1"/>`;
      });
    }

    // Clicks
    if (drawClicks) {
      Object.keys(clickdata).forEach((element) => {
        const color = element === unifiedlabel ? "#FF0000" : "#000000";
        const clicks = zip([
          clickdata[element]["relativeclickx"],
          clickdata[element]["relativeclicky"],
        ]);

        clicks.forEach((click) => {
          svg += `<rect x="${(click[0] - 2) * scaleFactor}" y="${(click[1] - 2) * scaleFactor}" 
                width="${5 * scaleFactor}" height="${5 * scaleFactor}" 
                fill="none" stroke="${color}" stroke-width="1"/>`;
        });
      });
    }

    svg += "</svg>";

    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${partitionlabel}_ui-analysis.svg`;
    a.click();

    URL.revokeObjectURL(url);
  }

  let canvas;
  let ctx;

  let allrects = [];

  let uidata = {
    windowdata: { maxheight: 0, maxwidth: 0, windowheight: 0, windowwidth: 0 },
    clickdata: [],
    uielements: [],
  };
  let windowdata = uidata.windowdata;
  let clickdata = uidata.clickdata;
  let screenshot;
  let loading = false;

  let zip = (rows) => rows[0].map((_, c) => rows.map((row) => row[c]));

  $: {
    if (partitionlabel && $pmfilters[analysisid]) {
      getUIClicks(partitionlabel);
      getScreenshot(partitionlabel);
    }
  }

  $: {
    if (scale && canvas) {
      drawButtons(allrects, windowdata, clickdata, screenshot);
    }
  }

  $: {
    if (triggerUpdate) {
      getScreenshot(partitionlabel);
      triggerUpdate = false;
    }
  }

  $: {
    if (canvas) {
      drawUIElements;
      drawScreenshot;
      drawClicks;
      unifiedlabel;
      drawButtons(allrects, windowdata, clickdata, screenshot);
    }
  }

  async function getScreenshot() {
    screenshot = null;
    fetchWithSession(
      `/api/analysis/loadScreenshot?analysisid=${analysisid}&partitionlabel=${partitionlabel}`,
      {
        method: "GET",
      }
    )
      .then((response) => response.json())
      .then((data) => {
        data = data.screenshot;
        if (data) {
          screenshot = new Image();
          screenshot.onload = function () {
            canvas.width = screenshot.width;
            canvas.height = screenshot.height;
          };
          screenshot.src = data;
          screenshot = screenshot;
        } else {
          canvas.width = windowdata.windowwidth;
          canvas.height = windowdata.windowheight;
        }
      });
  }

  async function getUIClicks(partitionlabel) {
    loading = true;
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    const res = await fetchWithSession(
      `/api/analysis/windowPartitionDetails?analysisid=${analysisid}&partitionlabel=${partitionlabel}&filter=${filterString}`
    );
    const text = await res.json();
    uidata = text;
    allrects = uidata.uielements;
    windowdata = uidata.windowdata;
    clickdata = uidata.clickdata;
    loading = false;
    if (res.ok) {
      return text;
    } else {
      throw new Error(text);
    }
  }

  function drawButtons(allrects, windowdata, clickdata, screenshot) {
    if (screenshot && screenshot.complete && drawScreenshot) {
      ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(screenshot, 0, 0);
    } else {
      ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    ctx.save();
    ctx.scale(scale / 50, scale / 50);

    ctx.strokeRect(0, 0, windowdata.windowwidth, windowdata.windowheight);

    if (drawClicks) {
      Object.keys(clickdata).forEach((element) => {
        if (element == unifiedlabel) {
          ctx.strokeStyle = "#FF0000";
        } else {
          ctx.strokeStyle = "#000000";
        }
        let clicks = zip([
          clickdata[element]["relativeclickx"],
          clickdata[element]["relativeclicky"],
        ]);
        clicks.forEach((click) => {
          ctx.strokeRect(click[0] - 2, click[1] - 2, 5, 5);
        });
      });
    }
    ctx.strokeStyle = "#000000";

    if (drawUIElements) {
      allrects.forEach((rect) => {
        ctx.strokeStyle = "#0000FF";
        ctx.strokeRect(rect.positionx, rect.positiony, rect.width, rect.height);
      });
    }

    ctx.restore();
  }
</script>

<!-- <div class="view_wrapper"> -->

{#if loading}
  <div class="mask_wrapper" style="height: 100%;">
    <Loading withOverlay={false} />
  </div>
{/if}
<div class="mask_wrapper" style={"display:" + (loading ? "none" : "flex")}>
  <canvas bind:this={canvas} />
</div>

<style>
  .mask_wrapper {
    display: flex;
    width: 100%;
    max-width: 100%;
    align-items: center;
    justify-content: center;
    /* overflow: scroll; */
  }

  canvas {
    max-width: 100%;
    max-height: 80vh;
  }

  canvas {
    border: 1px solid #d9d9d9;
  }
</style>
